/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Dashboard
 *| (C) Copyright IBM Corp. 2020
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */

import StringResources from '../nls/StringResources';

const ACTION_ID = 'deleteFilterAction';

export default class DeleteFilterAction {

	/**
	 * Constructor
	 *
	 * @param dashboard - dashboard API
	 * @param content - content API of the current visualization of interest
	 * @param metadataColumn - The metadataColumn this filter to be deleted.
	 * @param filterId (optional) - The uniqueId of the filter to be deleted.
	 */
	constructor(dashboard, content, metadataColumn, filterId, itemId) {
		this.content = content;
		this.dashboard = dashboard;
		this.metadataColumn = metadataColumn;
		this.filterId = filterId;
		this.itemId = itemId;

		this.transactionApi = dashboard.getFeature('Transaction');
		this.localFilters = this.content.getFeature('Visualization').getLocalFilters();

		this.toolbarContribution = [
			{
				name: 'delete',
				text: StringResources.get('toolbarDeleteFilterActionText'),
				label: StringResources.get('toolbarDeleteFilterActionText'),
				icon: 'dashboard-delete',
				type: 'Button',
				removeOnActionApplied: true,
				action: this.deleteLocalFilter.bind(this),
				group: ACTION_ID
			}
		];
	}

	/**
	 * This method will return the action id for this action (such as aggregationTypeAction, sortAction, etc....)
	 * @returns action id - a string describing this particular action.
	 */
	getActionId() {
		return ACTION_ID;
	}

	/**
	 * get the set of availableSort actions setting the selected state to match
	 * the state of the data.
	 * @returns the sort actions and their state.
	 */
	getAvailableActions() {
		return this.toolbarContribution;
	}

	/**
	 * Delete the filter defined in the filterId attribute or by metadataColumn.
	 * Delete by filterId allows support for 'AdditionalFilters' (ie: multiple keeps/excludes on the same item).
	 */
	deleteLocalFilter() {
		const filterKey = this.filterId ? {
			id: this.filterId
		} : this._getFilterKeyFromMetadataColumn();

		if (filterKey) {
			if (this.metadataColumn && this.metadataColumn.getType() === 'fact') {
				filterKey.itemId = this.metadataColumn.getId();
				filterKey.aggregationType = this.metadataColumn.getDefaultAggregation();
			} else {
				//The new removeFilter API uses 'itemId' as the filter key
				filterKey.itemId = this.itemId || filterKey.id;
			}

			if (this.metadataColumn && this.metadataColumn.isFilter()) {
				filterKey.filterType = 'filter';
			}

			const transactionToken = this.transactionApi.startTransaction();
			this.localFilters.removeFilter(filterKey, transactionToken);
			this.transactionApi.endTransaction(transactionToken);
		}
	}

	//When using metadataColumn, delete the first filter with this columnId and aggregation type.
	//TODO: Kept for backwards compatibility.  Delete by columnId is likely deprecated.
	_getFilterKeyFromMetadataColumn() {
		const filterToDelete = this.metadataColumn.getId();
		if (filterToDelete) {
			if (this.metadataColumn.getType() === 'fact') {
				return this.localFilters.getOrdinalFilterKey( /*itemId*/ filterToDelete, this.metadataColumn.getDefaultAggregation());
			} else {
				return this.localFilters.getCategoryFilterKey( /*itemId*/ filterToDelete);
			}
		}
		return null;
	}
}


// WEBPACK FOOTER //
// ./src/js/actions/DeleteFilterAction.js